import clsx from 'clsx';
import Image, { type ImageProps } from 'next/image';
import type React from 'react';
import styles from './NextImageView.module.scss';

type BaseProps = {
  /** 画像要素に適用するクラス名 */
  className?: string;
  /** コンテナ要素に適用するクラス名（fill時のデフォルトスタイルを上書き） */
  containerClassName?: string;
  /** 画像のobject-fitプロパティ @default 'cover' */
  objectFit?: 'contain' | 'cover';
};

/**
 * fill={true}の場合のprops
 * 親要素のサイズに合わせて画像を表示する場合に使用
 * sizesは必須（レスポンシブ最適化のため）
 */
type FillImageProps = BaseProps &
  ImageProps & {
    fill?: true;
    /** レスポンシブ画像のサイズ指定（必須）。getImageLayoutSizes()の使用を推奨 */
    sizes: ImageProps['sizes'];
  };

/**
 * fill={false}の場合のprops
 * 固定サイズで画像を表示する場合に使用
 */
type StaticImageProps = BaseProps &
  ImageProps & {
    fill: false;
    /** 画像の幅 */
    width: ImageProps['width'];
    /** 画像の高さ */
    height: ImageProps['height'];
    /** レスポンシブ画像のサイズ指定（任意だがパフォーマンス最適化のため推奨） */
    sizes?: ImageProps['sizes'];
  };

type NextImageViewProps = FillImageProps | StaticImageProps;

/**
 * Next.jsのImageコンポーネントをラップした画像表示コンポーネント
 *
 * ## 使い分け
 * - `fill={true}`: 親要素のサイズに追従させたい場合（カード内画像など）
 * - `fill={false}`: 固定サイズで表示したい場合（アイコン、サムネイルなど）
 *
 * * Next.js 公式ドキュメント (Props 詳細)
 * @see {@link https://nextjs.org/docs/app/api-reference/components/image}
 */
const NextImageView: React.FC<NextImageViewProps> = (props) => {
  const { className, containerClassName, objectFit = 'cover', fill, ...rest } = props;

  const isInternalStaticAsset = typeof rest.src === 'string' && rest.src.startsWith('/_next/static/');
  const unoptimized = rest.unoptimized || isInternalStaticAsset;

  const containerClass = clsx(
    styles.container,
    fill && !containerClassName && styles.containerFill,
    containerClassName,
  );
  return (
    <div className={containerClass}>
      <Image
        {...(rest as ImageProps)}
        fill={fill}
        unoptimized={unoptimized}
        className={clsx(styles[objectFit], styles.image, className)}
      />
    </div>
  );
};

export default NextImageView;
